import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo, OptimizedImage } from "@/components/ui/optimized-image";
import type { BrandCardData, Company } from "@/lib/types";

interface BrandCardProps {
  brand: BrandCardData;
  company?: Company;
}

function formatCategoryLabel(slug: string): string {
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function BrandCard({ brand, company }: BrandCardProps) {
  const companyName = company?.name || "Unknown Company";

  if (!brand || !brand.slug || !brand.name) {
    return null;
  }

  return (
    <Link href={`/brands/${brand.slug}`}>
      <Card className="h-full hover:shadow-lg hover:-translate-y-1 hover:border-accent/30 transition-all group border-border p-0">
        <div className="relative h-36 w-full bg-gradient-to-br from-muted to-background dark:from-white/35 dark:to-white/35 overflow-hidden rounded-t-lg">
          {brand.logo && (
            <OptimizedImage
              src={brand.logo}
              alt={brand.name}
              fallbackSrc="/placeholder.svg"
              fill
              className="object-contain"
              sizes="(max-width: 640px) 90vw, (max-width: 1024px) 40vw, (max-width: 1280px) 30vw, 20vw"
              priority={true}
            />
          )}
          {brand.categorySlug && (
            <span className="absolute top-2 left-2 text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground px-2 py-1 rounded-md border border-border/80 shadow-sm">
              {formatCategoryLabel(brand.categorySlug)}
            </span>
          )}
        </div>
        <CardContent className="p-4 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-foreground group-hover:text-accent transition-colors">
                {brand.name}
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Owned by {companyName}
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>
          <p className="text-sm text-muted-foreground mt-4 line-clamp-2 leading-relaxed">
            {brand.description}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {brand.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
